import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { NotificationService } from 'app/services/notification.service';
import { NativePermissionsService } from 'app/services/native-permissions.service';
import { NetworkService } from 'app/services/network.service';

import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { environment } from '../environments/environment';

import { OrientationService } from 'app/services/orientation.service';
import { AppService } from './services/app.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UpdateModalComponent } from './modules/shared/update-modal/update-modal.component';
import { AppDownloadModalComponent } from './modules/shared/app-download-modal/app-download-modal.component';
import { NoConnectionModalComponent } from './modules/shared/no-connection-modal/no-connection-modal.component';
import { TourService } from './services/tour.service';


@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        RouterOutlet
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    host: { 'class': 'flex flex-col h-full w-full' }
})
export class AppComponent implements OnInit {
    loading = false;
    private notificationService = inject(NotificationService);
    private nativePermissionsService = inject(NativePermissionsService);
    private isNative = Capacitor.isNativePlatform();
    private orientationService = inject(OrientationService);

    readonly modal = inject(MatDialog);
    dialogRef: MatDialogRef<UpdateModalComponent> | null = null;
    downloadDialogRef: MatDialogRef<AppDownloadModalComponent> | null = null;
    noConnectionDialogRef: MatDialogRef<NoConnectionModalComponent> | null = null;

    private networkService = inject(NetworkService);

    // Variable de testing para desarrollo - cambiar a true para forzar el modal
    // En producción siempre se ignora esta variable
    private FORCE_SHOW_DOWNLOAD_MODAL_FOR_TESTING = false;

    constructor(
        private router: Router,
        private appService: AppService,
        private tourService: TourService
    ) {
        this.setupBackButtonHandler();
    }

    ngOnInit() {
        const app = initializeApp(environment.firebase);
        getAnalytics(app);

        this.setupDeviceOrientation();

        this.initializeNotificationsForPlatform().then(r => {
            console.log('Notificaciones inicializadas correctamente');
        })
            .catch(error => {
                console.error('Error al inicializar notificaciones:', error);
            });

        this.appService.onVersionUpdate.subscribe(updateInfo => {
            if (updateInfo?.type && this.isNative) {
                this.showModal(updateInfo.type);
            }
        });

        // Mostrar modal de descarga en Android web
        this.checkAndShowDownloadModal();

        // Monitorear estado de conexión (solo en plataformas nativas)
        if (this.isNative) {
            this.networkService.connectionStatus$.subscribe(isConnected => {
                if (!isConnected) {
                    this.showNoConnectionModal();
                } else {
                    this.closeNoConnectionModal();
                }
            });
        }

    }

    ngAfterViewInit() {
    }

    private async setupDeviceOrientation(): Promise<void> {
        try {
            await this.orientationService.setupOrientationForDevice();
        } catch (error) {
            console.error('Error configurando orientación del dispositivo:', error);
        }
    }

    /**
     * Inicializa el sistema de notificaciones según la plataforma
     */
    private async initializeNotificationsForPlatform(): Promise<void> {
        try {
            if (this.isNative) {
                console.log(`Inicializando en plataforma nativa: ${Capacitor.getPlatform()}`);

                // En dispositivos nativos, solicitar permisos después de una interacción del usuario
                // o verificar si ya están concedidos
                const hasPermissions = await this.nativePermissionsService.checkPushNotificationPermission();
                console.log('🔐 ¿Tiene permisos existentes?', hasPermissions);

                if (hasPermissions) {
                    console.log('✅ Ya tiene permisos, inicializando...');
                    await this.notificationService.requestPermission();
                }

            } else {
                // En web, verificar si ya hay permiso para notificaciones
                if (Notification.permission === 'granted') {
                    await this.notificationService.requestPermission();
                    console.log('Notificaciones web inicializadas correctamente');
                }
            }
        } catch (error) {
            console.error('Error al inicializar notificaciones:', error);
        }
    }


    private setupBackButtonHandler() {
        // Only set up back button handler on native platforms
        if (Capacitor.isNativePlatform()) {
            App.addListener('backButton', (data) => {
                this.handleBackButton();
            });
        }
    }

    private handleBackButton() {
        const currentUrl = this.router.url;

        const exitRoutes = ['/learn', '/practice', '/', '/sign-in'];

        if (exitRoutes.includes(currentUrl)) {
            App.exitApp();
        } else {
            window.history.back();
        }
    }

    showModal(type: string) {
        if (this.dialogRef) return;
        this.dialogRef = this.modal.open(UpdateModalComponent, {
            data: type,
            disableClose: type == 'force' ? true : false,
        });

        this.dialogRef.afterClosed().subscribe(result => {
            this.dialogRef = null; // Reinicia la referencia cuando se cierra
        })
    }

    private async checkAndShowDownloadModal(): Promise<void> {
        try {
            // En producción, SIEMPRE mostrar el modal (ignorar variable de testing)
            // En desarrollo, usar la variable de testing
            const shouldForce = !environment.production && this.FORCE_SHOW_DOWNLOAD_MODAL_FOR_TESTING;

            // Verificar si ya se mostró el modal anteriormente
            if (!shouldForce && !environment.production) {
                const hasBeenPrompted = localStorage.getItem('app_download_prompted');
                if (hasBeenPrompted) {
                    console.log('Modal de descarga ya fue mostrado anteriormente');
                    return;
                }
            }

            if (shouldForce) {
                console.log('🔧 [DEV] Forzando visualización del modal de descarga (FORCE_SHOW_DOWNLOAD_MODAL_FOR_TESTING = true)');
            }

            // En producción, verificar localStorage normalmente
            if (environment.production) {
                const hasBeenPrompted = localStorage.getItem('app_download_prompted');
                if (hasBeenPrompted) {
                    console.log('Modal de descarga ya fue mostrado anteriormente');
                    return;
                }
            }

            // Verificar si es Android web
            const isAndroidWeb = this.nativePermissionsService.isAndroidWeb();
            if (!isAndroidWeb) {
                console.log('No es Android web, no se muestra modal de descarga');
                return;
            }

            // Mostrar modal
            console.log('Mostrando modal de descarga de app Android');
            this.showDownloadModal();

        } catch (error) {
            console.error('Error al verificar condiciones para modal de descarga:', error);
        }
    }

    private showDownloadModal(): void {
        if (this.downloadDialogRef) return;

        this.downloadDialogRef = this.modal.open(AppDownloadModalComponent, {
            disableClose: false, // Permitir cerrar con back button
            panelClass: 'app-download-modal-panel'
        });

        this.downloadDialogRef.afterClosed().subscribe(() => {
            this.downloadDialogRef = null;
        });
    }

    private showNoConnectionModal(): void {
        if (this.noConnectionDialogRef) return;

        this.noConnectionDialogRef = this.modal.open(NoConnectionModalComponent, {
            disableClose: true, // No permitir cerrar hasta que haya conexión
            panelClass: 'no-connection-modal-panel',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
        });

        this.noConnectionDialogRef.afterClosed().subscribe(() => {
            this.noConnectionDialogRef = null;
        });
    }

    private closeNoConnectionModal(): void {
        if (this.noConnectionDialogRef) {
            this.noConnectionDialogRef.close();
            this.noConnectionDialogRef = null;
        }
    }

}
